import React, { useState, useEffect } from 'react';
import { Bell, MessageCircle, Send, Settings, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';

export const TelegramNotificationSettings: React.FC = () => {
  const defaultSettings = {
    telegram_enabled: true,
    email_enabled: true,
    admin_chat_id: '8039920869',
    sales_chat_id: '8039920869',
    production_chat_id: '8039920869',
    notification_types: {
      new_offer: true,
      offer_accepted: true,
      offer_rejected: true,
      production_start: true,
      production_complete: true,
      low_stock: true,
      daily_report: true,
      system_errors: true
    }
  };

  const [settings, setSettings] = useState(defaultSettings);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [botInfo, setBotInfo] = useState<any>(null);
  const [testSending, setTestSending] = useState(false);

  // Pobierz ustawienia
  useEffect(() => {
    fetchSettings();
    testBotConnection();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/telegram/settings`);
      // Merge response z defaultami na wypadek brakujących pól
      const mergedSettings = {
        ...defaultSettings,
        ...response.data,
        notification_types: {
          ...defaultSettings.notification_types,
          ...(response.data.notification_types || {})
        }
      };
      setSettings(mergedSettings);
    } catch (error: any) {
      console.error('Błąd pobierania ustawień:', error);
      // Jeśli endpoint nie istnieje, użyj domyślnych ustawień
      if (error.response?.status === 404) {
        console.log('Endpoint nie znaleziony, używam domyślnych ustawień');
        setSettings(defaultSettings);
      }
    } finally {
      setLoading(false);
    }
  };

  const testBotConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/test-connection`);
      if (response.data.success) {
        setBotInfo(response.data.bot);
      }
    } catch (error) {
      console.error('Błąd połączenia z botem:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/telegram/settings`, settings);
      alert('Ustawienia zapisane!');
    } catch (error) {
      console.error('Błąd zapisywania:', error);
      alert('Błąd zapisywania ustawień');
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async (type: string, chatId: string) => {
    if (!chatId) {
      alert('Wprowadź Chat ID');
      return;
    }

    setTestSending(true);
    try {
      await axios.post(`${API_URL}/api/telegram/test`, {
        type,
        chatId
      });
      alert('Powiadomienie testowe wysłane!');
    } catch (error) {
      console.error('Błąd wysyłania:', error);
      alert('Błąd wysyłania powiadomienia');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800 rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-blue-500" />
          Powiadomienia Telegram
        </h2>
        
        {botInfo && (
          <div className="bg-green-900/50 border border-green-700 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span>Bot połączony: @{botInfo.username}</span>
            </div>
          </div>
        )}
      </div>

      {/* Główne przełączniki */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <label className="flex items-center gap-3 p-4 bg-zinc-700 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={settings.telegram_enabled}
            onChange={(e) => setSettings({...settings, telegram_enabled: e.target.checked})}
            className="w-5 h-5 text-orange-500"
          />
          <div>
            <p className="font-medium text-white">Powiadomienia Telegram</p>
            <p className="text-sm text-gray-400">Otrzymuj alerty przez Telegram</p>
          </div>
        </label>

        <label className="flex items-center gap-3 p-4 bg-zinc-700 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={settings.email_enabled}
            onChange={(e) => setSettings({...settings, email_enabled: e.target.checked})}
            className="w-5 h-5 text-orange-500"
          />
          <div>
            <p className="font-medium text-white">Powiadomienia Email</p>
            <p className="text-sm text-gray-400">Otrzymuj alerty przez email</p>
          </div>
        </label>
      </div>

      {/* Chat IDs */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-semibold text-white">Konfiguracja kanałów</h3>
        
        <div className="bg-zinc-700 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-2">
            Aby otrzymać swój Chat ID:
          </p>
          <ol className="text-sm text-gray-300 space-y-1">
            <li>1. Rozpocznij czat z @plexisystem_ai_bot</li>
            <li>2. Wyślij komendę /start</li>
            <li>3. Bot wyśle Ci Twój Chat ID</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Administracja
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={settings.admin_chat_id}
                onChange={(e) => setSettings({...settings, admin_chat_id: e.target.value})}
                placeholder="Chat ID"
                className="flex-1 px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg text-white"
              />
              <button
                onClick={() => sendTestNotification('daily_report', settings.admin_chat_id)}
                disabled={testSending || !settings.admin_chat_id}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Dział handlowy
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={settings.sales_chat_id}
                onChange={(e) => setSettings({...settings, sales_chat_id: e.target.value})}
                placeholder="Chat ID"
                className="flex-1 px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg text-white"
              />
              <button
                onClick={() => sendTestNotification('new_offer', settings.sales_chat_id)}
                disabled={testSending || !settings.sales_chat_id}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Produkcja
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={settings.production_chat_id}
                onChange={(e) => setSettings({...settings, production_chat_id: e.target.value})}
                placeholder="Chat ID"
                className="flex-1 px-3 py-2 bg-zinc-600 border border-zinc-500 rounded-lg text-white"
              />
              <button
                onClick={() => sendTestNotification('daily_report', settings.production_chat_id)}
                disabled={testSending || !settings.production_chat_id}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Typy powiadomień */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-semibold text-white">Typy powiadomień</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.new_offer || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, new_offer: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Nowe oferty</p>
              <p className="text-xs text-gray-400">Powiadomienia o utworzeniu nowej oferty</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.offer_accepted || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, offer_accepted: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Oferty zaakceptowane</p>
              <p className="text-xs text-gray-400">Powiadomienia o akceptacji oferty</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.offer_rejected || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, offer_rejected: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Oferty odrzucone</p>
              <p className="text-xs text-gray-400">Powiadomienia o odrzuceniu oferty</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.production_start || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, production_start: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Start produkcji</p>
              <p className="text-xs text-gray-400">Powiadomienia o rozpoczęciu produkcji</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.production_complete || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, production_complete: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Koniec produkcji</p>
              <p className="text-xs text-gray-400">Powiadomienia o zakończeniu produkcji</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.low_stock || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, low_stock: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Niskie stany magazynowe</p>
              <p className="text-xs text-gray-400">Alerty o niskim stanie magazynowym</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.daily_report || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, daily_report: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Raporty dzienne</p>
              <p className="text-xs text-gray-400">Codzienne podsumowanie aktywności</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notification_types?.system_errors || false}
              onChange={(e) => setSettings({
                ...settings,
                notification_types: {...settings.notification_types, system_errors: e.target.checked}
              })}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Błędy systemowe</p>
              <p className="text-xs text-gray-400">Alerty o błędach i problemach</p>
            </div>
          </label>
        </div>
      </div>

      {/* Przyciski akcji */}
      <div className="flex gap-4">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              Zapisywanie...
            </>
          ) : (
            <>
              <Settings className="w-4 h-4" />
              Zapisz ustawienia
            </>
          )}
        </button>

        <a
          href="https://t.me/plexisystem_ai_bot"
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          Otwórz bot
        </a>
      </div>

      {/* Informacja o webhook */}
      <div className="mt-6 p-4 bg-zinc-700 rounded-lg">
        <p className="text-sm text-gray-300">
          <strong>Status webhook:</strong> Aktywny
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Bot automatycznie odbiera i przetwarza komendy. Dostępne komendy:
          /start, /help, /status, /raport, /oferty, /produkcja, /magazyn
        </p>
      </div>
    </div>
  );
};